import { createContext, useContext, useState, ReactNode } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import * as solid from "@fortawesome/free-solid-svg-icons";
import * as regular from "@fortawesome/free-regular-svg-icons";

export type IconPack = "solid" | "regular";

interface IconPackContextProps {
  iconPack: IconPack;
  setIconPack: (pack: IconPack) => void;
  getIcon: (name: string) => IconProp;
}

const IconPackContext = createContext<IconPackContextProps | null>(null);

export const IconPackProvider = ({ children }: { children: ReactNode }) => {
  const [iconPack, setIconPack] = useState<IconPack>(
    (localStorage.getItem("iconPack") as IconPack) || "solid"
  );

  const nameMap: Record<string, string> = {
    pen: "pen-to-square",
    edit: "pen-to-square",
    trash: "trash-can",
    save: "floppy-disk",
    file: "file-lines",
  };

  const forceSolid: string[] = [
    "plus",
    "bars",
    "filter",
    "xmark",
    "sort",
    "sort-up",
    "sort-down",
    "link",
  ];

  const getIcon = (name: string): IconProp => {
    const mappedName = iconPack === "regular" && nameMap[name] ? nameMap[name] : name;

    const pascal = `fa${mappedName
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("")}`;

    const lib = forceSolid.includes(name)
      ? solid
      : iconPack === "regular"
      ? regular
      : solid;

    const icon = (lib as any)[pascal];

    if (icon) {
      return [forceSolid.includes(name) ? "fas" : iconPack === "regular" ? "far" : "fas", mappedName] as IconProp;
    }

    return ["fas", "question-circle"];
  };

  const handleSetIconPack = (pack: IconPack) => {
    setIconPack(pack);
    localStorage.setItem("iconPack", pack);
  };

  return (
    <IconPackContext.Provider value={{ iconPack, setIconPack: handleSetIconPack, getIcon }}>
      {children}
    </IconPackContext.Provider>
  );
};

export const useIconPack = () => {
  const ctx = useContext(IconPackContext);
  if (!ctx) throw new Error("useIconPack must be used within IconPackProvider");
  return ctx;
};
